import { useState } from "react";
import { BookOpen, Pencil, Plus, Trash2 } from "lucide-react";
import { deletePlaybook, getPlaybooks, useQuery } from "wasp/client/operations";
import useTranslation from "../../../client/hooks/useTranslation";
import { useToast } from "../../../client/hooks/use-toast";
import { Button } from "../../../client/components/ui/button";
import PlaybookForm from "../../../playbook/PlaybookForm";
import type { PlaybookSession, PlaybookSetup } from "../../../playbook/constants";
import { useLiteData } from "../../data/LiteDataProvider";
import { Card, cardCls, Empty } from "../../ui/ui";
import { EdgyLockCard } from "./EdgyLockCard";
import { HowItWorks } from "./HowItWorks";

const chipCls = "text-[11px] font-medium px-2 py-0.5 rounded-full border border-border bg-muted/50 text-foreground";

/** Fiche d'un playbook : mêmes informations que la carte Advanced (+ les sessions). */
function PlaybookItem({ playbook, onEdit, onDelete }: { playbook: any; onEdit: () => void; onDelete: () => void }) {
  const T = useTranslation();
  const t = T.playbook;
  const { fmt } = useLiteData();
  const setups = (playbook.setups as PlaybookSetup[] | null) ?? [];
  const sessions = (playbook.sessions as PlaybookSession[] | null) ?? [];
  const targets = [...(playbook.targetAccounts ?? []), ...(playbook.targetGroups ?? [])] as { id: string; name: string }[];

  return (
    <section className={`${cardCls} p-5 space-y-4`}>
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="w-9 h-9 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center"><BookOpen size={17} /></span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold truncate">{playbook.name ?? playbook.strategyName}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t.versionLabel.replace("{version}", String(playbook.version))} · {t.createdOn.replace("{date}", new Date(playbook.createdAt).toLocaleDateString(fmt.locale))}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} aria-label={t.edit} title={t.edit}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={onDelete} aria-label={t.deleteAction} title={t.deleteAction}><Trash2 className="h-4 w-4 text-destructive" /></Button>
        </div>
      </header>

      {playbook.markets?.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t.strategyStep.marketsLabel}</div>
          <div className="flex flex-wrap gap-1.5">{playbook.markets.map((m: string) => <span key={m} className={`${chipCls} font-mono`}>{m}</span>)}</div>
        </div>
      )}

      {sessions.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t.strategyStep.sessionsLabel}</div>
          <div className="flex flex-wrap gap-1.5">
            {sessions.map((s, i) => <span key={i} className={chipCls}>{s.name} <span className="font-mono text-muted-foreground">{s.startTime}-{s.endTime}</span></span>)}
          </div>
        </div>
      )}

      {targets.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t.targetsLabel}</div>
          <div className="flex flex-wrap gap-1.5">{targets.map(x => <span key={x.id} className={chipCls}>{x.name}</span>)}</div>
        </div>
      )}

      {setups.length > 0 && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t.setupsStep.setupsLabel}</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {setups.map((s, i) => (
              <div key={i} className="rounded-xl border border-border bg-muted/30 p-3 min-w-0">
                <p className="text-sm font-medium break-words">{s.name}</p>
                {s.entryRules && <p className="text-xs text-muted-foreground mt-1 break-words">{s.entryRules}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {playbook.personalRules && (
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1.5">{t.rulesStep.personalRulesLabel}</div>
          <p className="text-sm text-muted-foreground whitespace-pre-line break-words">{playbook.personalRules}</p>
        </div>
      )}
    </section>
  );
}

/**
 * Playbooks : liste, création, édition (formulaire Advanced en 3 étapes),
 * suppression. Ouvert à tous les plans, comme dans l'Advanced.
 */
export function PlaybookTab({ isLocked, onUpgrade }: { isLocked: boolean; onUpgrade: () => void }) {
  const T = useTranslation();
  const t = T.lite.edgy;
  const { toast } = useToast();
  const { data: playbooks, isLoading, refetch } = useQuery(getPlaybooks);
  // null = liste ; "new" = création ; sinon le playbook en cours d'édition.
  const [editing, setEditing] = useState<"new" | any | null>(null);

  // Même confirmation et mêmes messages que PlaybookPage.
  async function handleDelete(playbook: any) {
    if (!window.confirm(T.playbook.deleteConfirm)) return;
    try {
      await deletePlaybook({ id: playbook.id });
      toast({ description: T.playbook.deletedToast });
      refetch();
    } catch (err: any) {
      toast({ variant: "destructive", description: err?.message || T.playbook.deleteError });
    }
  }

  if (editing) {
    const name = editing === "new" ? "" : editing.name ?? editing.strategyName;
    return (
      <Card title={editing === "new" ? t.playbook.newTitle : t.playbook.editTitle.replace("{name}", name)}>
        <PlaybookForm
          embedded
          playbook={editing === "new" ? undefined : editing}
          onSaved={() => { setEditing(null); refetch(); }}
          onCancel={() => setEditing(null)}
        />
      </Card>
    );
  }

  const list = (playbooks ?? []) as any[];

  return (
    <div className="space-y-5">
      {isLocked ? <EdgyLockCard onUpgrade={onUpgrade} note={t.lock.playbookNote} /> : <HowItWorks />}

      <Card
        title={t.playbook.listTitle}
        subtitle={list.length ? t.playbook.count.replace("{count}", String(list.length)) : T.playbook.subtitle}
        action={list.length > 0 ? (
          <Button size="sm" variant="outline" onClick={() => setEditing("new")} className="shrink-0">
            <Plus className="h-4 w-4 mr-1" />{T.playbook.newPlaybook}
          </Button>
        ) : undefined}
      >
        {isLoading ? (
          <Empty title={T.lite.common.loading} />
        ) : !list.length ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center"><BookOpen size={20} /></span>
            <div className="text-sm font-medium">{T.playbook.emptyTitle}</div>
            <p className="text-sm text-muted-foreground max-w-md">{T.playbook.emptyDescription}</p>
            <Button size="sm" onClick={() => setEditing("new")}><Plus className="h-4 w-4 mr-1" />{T.playbook.emptyCta}</Button>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {list.map(p => <PlaybookItem key={p.id} playbook={p} onEdit={() => setEditing(p)} onDelete={() => handleDelete(p)} />)}
          </div>
        )}
      </Card>
    </div>
  );
}
